import { formatPdfDate } from "../../../utils/hooks/dataHandler";

export const DEFAULT_UNABSORBED_SUMMARY = {
  caRows: [],
  lossRows: [],
  donationRows: [],
};

export const createExchangeRateResolver = (exchangeRate) => (rate) => {
  const parsedRate = parseFloat(rate);
  return parsedRate > 0 ? parsedRate : parseFloat(exchangeRate) || 1;
};

export const getCellContent = (cell) => {
  if (cell == null) return "";
  if (typeof cell === "object" && cell.content != null) return cell.content;
  return cell;
};

export const parsePdfAmount = (cell) => {
  const value = getCellContent(cell);
  if (value === "" || value == null) return null;

  const text = String(value).trim();
  if (!text) return null;

  const isNegative = /^\(.*\)$/.test(text);
  const numericText = text.replace(/[(),\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(numericText)) return null;

  const amount = Number(numericText);
  if (Number.isNaN(amount)) return null;

  return isNegative ? -Math.abs(amount) : amount;
};

export const sumCalculatorValues = (value) =>
  Array.isArray(value)
    ? value.reduce((sum, num) => sum + (parseFloat(num) || 0), 0)
    : parseFloat(value) || 0;

export const CROSS_YA_ROW_FIELDS = [
  "preceding_amount",
  "current_amount",
  "precedingHistoryAmount",
  "currentHistoryAmount",
  "crossYAOffset",
  "crossYAOffsetAppliedTo",
  "crossYAMismatchAmount",
];

export const omitCrossYARowFields = (row) => {
  if (!row) return row;

  const singleYARow = { ...row };
  CROSS_YA_ROW_FIELDS.forEach((field) => {
    delete singleYARow[field];
  });

  return singleYARow;
};

export const hasSplitAmountFields = (row) =>
  Object.prototype.hasOwnProperty.call(row || {}, "preceding_amount") ||
  Object.prototype.hasOwnProperty.call(row || {}, "current_amount");

export const getSplitAmountTotal = (row) => {
  const hasPrecedingAmount = Object.prototype.hasOwnProperty.call(
    row || {},
    "preceding_amount"
  );
  const hasCurrentAmount = Object.prototype.hasOwnProperty.call(
    row || {},
    "current_amount"
  );

  if (hasPrecedingAmount || hasCurrentAmount) {
    return (
      (parseFloat(row?.preceding_amount) || 0) +
      (parseFloat(row?.current_amount) || 0)
    );
  }

  return parseFloat(row?.amount) || 0;
};

export const getSingleYAAmount = (row) =>
  sumCalculatorValues(
    Array.isArray(row?.historyAmount) && row.historyAmount.length > 0
      ? row.historyAmount
      : row?.amount
  );

export const getAmountForTaxPeriod = (row, isCrossYA) =>
  isCrossYA ? getSplitAmountTotal(row) : getSingleYAAmount(row);

export const getUnutilisedAmount = (row, isCrossYA) =>
  getAmountForTaxPeriod(row, isCrossYA);

export const getTaxPreviouslyAssessedAmount = (row, isCrossYA) => {
  if (isCrossYA || hasSplitAmountFields(row)) {
    return getSplitAmountTotal(row);
  }

  if (Object.prototype.hasOwnProperty.call(row || {}, "historyAmount")) {
    return sumCalculatorValues(row?.historyAmount);
  }

  return getSingleYAAmount(row);
};

export const isAutoloadTaxPreviouslyAssessedRow = (row) =>
  row?.status === "autoload";

export const isManualTaxPreviouslyAssessedRow = (row) =>
  row?.status !== "autoload" &&
  Boolean(
    row?.date ||
      getTaxPreviouslyAssessedAmount(row) !== 0 ||
      (hasSplitAmountFields(row) &&
        ((parseFloat(row?.preceding_amount) || 0) !== 0 ||
          (parseFloat(row?.current_amount) || 0) !== 0))
  );

export const isManualTaxPreviouslyAssessedRowForYA = (row, amountKey) => {
  if (!isManualTaxPreviouslyAssessedRow(row)) return false;
  if (!hasSplitAmountFields(row)) return true;
  if (!row.date) return (parseFloat(row[amountKey]) || 0) !== 0;

  const historyKey =
    amountKey === "preceding_amount"
      ? "precedingHistoryAmount"
      : "currentHistoryAmount";

  return (
    (parseFloat(row[amountKey]) || 0) !== 0 ||
    Boolean(row[historyKey]?.length) ||
    // Keep dated legacy rows that have no amount or history for either YA.
    (!(parseFloat(row.preceding_amount) || 0) &&
      !(parseFloat(row.current_amount) || 0) &&
      !row.precedingHistoryAmount?.length &&
      !row.currentHistoryAmount?.length)
  );
};

export const getSignedTaxPreviouslyAssessedAmount = (row, amount) => {
  const absoluteAmount = Math.abs(parseFloat(amount) || 0);
  if (!row?.taxAdjustment) return -absoluteAmount;
  return row.taxAdjustment === "Deduct" ? -absoluteAmount : absoluteAmount;
};

export const calculateTaxPreviouslyAssessedTotal = (
  rows = [],
  getAmount = (row) => getTaxPreviouslyAssessedAmount(row)
) =>
  rows.reduce(
    (sum, row) =>
      sum + getSignedTaxPreviouslyAssessedAmount(row, getAmount(row)),
    0
  ) || 0;

export const calculateAutoloadTaxSetOff = (
  rows = [],
  getAmount = (row) => getTaxPreviouslyAssessedAmount(row)
) =>
  calculateTaxPreviouslyAssessedTotal(
    rows.filter(isAutoloadTaxPreviouslyAssessedRow),
    getAmount
  );

export const getManualTaxPreviouslyAssessedRows = (
  rows = [],
  getAmount = (row) => getTaxPreviouslyAssessedAmount(row)
) =>
  rows.filter(isManualTaxPreviouslyAssessedRow).map((row) => ({
    label: row.date
      ? `Tax previously assessed on ${formatPdfDate(row.date)}`
      : "Tax previously assessed",
    amount: -Math.abs(parseFloat(getAmount(row)) || 0),
    row
  }));

export const calculateCorporateRebate = ({
  corporateIncomeTaxRebate,
  currentYearRebate,
  postDeductions
}) => {
  let rebate = 0;

  if (
    postDeductions > 0 &&
    corporateIncomeTaxRebate?.taxableRate &&
    corporateIncomeTaxRebate?.cappedAmount
  ) {
    rebate =
      Math.round(
        Math.min(
          postDeductions * (corporateIncomeTaxRebate.taxableRate / 100),
          Number(corporateIncomeTaxRebate.cappedAmount)
        ) * 100
      ) / 100;
  }

  if (!corporateIncomeTaxRebate?.isRebate) {
    return {
      checkValidCashRebate: false,
      grossRebate: rebate,
      netRebate: rebate,
      shouldShowCashGrant: false
    };
  }

  if (Math.abs(rebate) > Math.abs(currentYearRebate?.amount || 0)) {
    return {
      checkValidCashRebate: false,
      grossRebate: rebate,
      netRebate: Math.abs(rebate) - Math.abs(currentYearRebate?.amount || 0),
      shouldShowCashGrant: true
    };
  }

  return {
    checkValidCashRebate: rebate > 0,
    grossRebate: rebate,
    netRebate: 0,
    shouldShowCashGrant: false
  };
};

export const calculateExemption = (chargeableIncome, isStartup) => {
  const firstThreshold = isStartup ? 100000 : 10000;
  const secondThreshold = isStartup ? 100000 : 190000;
  const firstExemptAmount = isStartup
    ? Math.round(Math.min(chargeableIncome, 100000) * 0.75)
    : Math.round(Math.min(chargeableIncome, 10000) * 0.75);
  const secondExemptAmount = Math.round(
    isStartup
      ? Math.min(Math.max(chargeableIncome - 100000, 0), 100000) * 0.5
      : Math.min(Math.max(chargeableIncome - 10000, 0), 190000) * 0.5
  );

  return {
    firstExemptAmount,
    firstThreshold,
    secondExemptAmount,
    secondThreshold,
    totalExemptedAmount: firstExemptAmount + secondExemptAmount
  };
};

export const calculateTaxPayableBeforeEci = (summary = {}) =>
  Number(summary.postDeductions || 0) + Number(summary.corporateRebate || 0);

export const getUnutilisedAmountLabel = (row, formatter) =>
  row?.isForfeited ? "(Disregarded)" : formatter();

export const getActiveUnutilisedRows = (rows = []) =>
  rows.filter((row) => !row?.isForfeited);

export const isDeductAdjustmentRow = (row) =>
  row?.taxAdjustment === "Deduct" || Number(row?.amount ?? 0) < 0;

export const getPdfRowAmountByKey = (row, amountKey = "amount") => {
  if (Object.prototype.hasOwnProperty.call(row || {}, amountKey)) {
    return parseFloat(row?.[amountKey]) || 0;
  }

  return parseFloat(row?.amount) || 0;
};

export const getRoundedPdfRowAmountByKey = (row, amountKey = "amount") =>
  Math.round(getPdfRowAmountByKey(row, amountKey));

export const getSignedPdfAdjustmentAmount = (
  row,
  amountKey = "amount",
  { preserveCaSummarySign = true, round = true } = {}
) => {
  const getAmount = round ? getRoundedPdfRowAmountByKey : getPdfRowAmountByKey;

  if (preserveCaSummarySign && row?.row_id === "ca-summary-00") {
    return getAmount(row, amountKey);
  }

  const amount = Math.abs(getAmount(row, amountKey));
  return row?.taxAdjustment === "Deduct" ? -amount : amount;
};

export const getPdfDeductionAmount = (row, amountKey = "amount") =>
  -Math.abs(getPdfRowAmountByKey(row, amountKey));

export const getSignedUnutilisedLossAmount = (
  row,
  amountKey = "amount",
) => {
  const amount = Math.abs(getPdfRowAmountByKey(row, amountKey));

  if (row?.status === "autoload" && row?.taxAdjustment === "Added Back") {
    return amount;
  }

  return -amount;
};

export const getPdfAdditionAmount = (row, amountKey = "amount") =>
  Math.abs(getPdfRowAmountByKey(row, amountKey));

export const getRoundedPdfDeductionAmount = (row, amountKey = "amount") =>
  -Math.abs(getRoundedPdfRowAmountByKey(row, amountKey));

export const getRoundedPdfAdditionAmount = (row, amountKey = "amount") =>
  Math.abs(getRoundedPdfRowAmountByKey(row, amountKey));

export const sumPdfRows = (rows = [], getAmount, amountKey = "amount") =>
  rows.reduce((sum, row) => sum + getAmount(row, amountKey), 0);

export const getPdfRowName = (row) => row?.name || row?.description || "";

export const createPdfDetailRows = (
  rows = [],
  getAmount,
  getLabel,
  amountKey
) =>
  rows.map((row) => ({
    amount: getAmount(row, amountKey),
    exchangeRate: row.exchangeRate,
    label: getLabel(row),
    reference: row.reference,
    schedule: row.schedule,
  }));

export const createPdfSection = (
  lineById,
  id,
  rows,
  total,
  overrides = {}
) => ({
  ...lineById[id],
  rows,
  total,
  ...overrides,
});

export const createPdfResult = (lineById, id, amount, overrides = {}) => ({
  ...lineById[id],
  amount,
  ...overrides,
});

export const getPdfComputedAmount = (computedSummary, key, fallback) => {
  const value = computedSummary?.[key];
  return value === null || value === undefined || value === ""
    ? fallback
    : parseFloat(value) || 0;
};

export const hasPdfSectionActivity = (rows = [], total = 0) =>
  rows.length > 0 || Number(total || 0) !== 0;

export const createPdfLineById = (lines = []) =>
  lines.reduce(
    (lineById, line) => ({
      ...lineById,
      [line.id]: line,
    }),
    {},
  );

export const buildAgreementLabel = (row) =>
  row.toDeclareAgreement
    ? (row.toDeclareAgreement
        ? row.agreementStatus?.isAgreed
          ? " - Agreed"
          : " - Not Agreed"
        : "") +
      (row.agreementStatus?.dateOfNOA
        ? ` on ${formatPdfDate(row.agreementStatus?.dateOfNOA)}`
        : "")
    : "";
